<!--
=========================================================
* Vue Argon Dashboard 2 - v4.0.0
=========================================================

* Product Page: https://creative-tim.com/product/vue-argon-dashboard
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

=========================================================

* Application Copyright (c) 2025 VMilesUp (https://vmilesup.in)
* Developed for: Vardan Education and Charitable Trust (VECT)
* Inspired by & Tribute to: Creative Tim Argon Dashboard 2
=========================================================

MIT License

Copyright (c) 2023 Creative Tim (www.creative-tim.com)

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.

=========================================================
-->


<template>
  <!-- Global Overlays (Needed in all modes) -->
  <transition name="fade">
    <div v-if="showLoading" class="loading-overlay">
      <div class="text-center">
        <div v-if="loadingStatus" class="mb-2 animate__animated animate__fadeInDown">
            <span class="loading-status-badge">
                <i class="fas fa-satellite-dish fa-spin"></i> {{ loadingStatus }}
            </span>
        </div>
        <div class="w-100" style="aspect-ratio: 1; height: 180px;">
          <img :src="logo" alt="main_logo" class="navbar-brand-img h-100" />
        </div>
        <div class="w-100 d-flex justify-content-center mt-3">
          <i class="fas fa-spinner fa-spin text-white"></i>
        </div>
      </div>
    </div>
  </transition>

  <div v-if="!isOnline" class="offline-blocker">
    <div 
        class="offline-content shadow-lg border-danger draggable-card"
        :style="{ transform: `translate(${offlineCardPos.x}px, ${offlineCardPos.y}px)` }"
        @mousedown="startDrag"
        @touchstart.passive="startDrag"
    >
        <div class="drag-handle mb-2"><i class="fas fa-grip-lines text-muted"></i></div>
        <i class="fas fa-wifi-slash text-danger mb-3 fa-3x"></i>
        <h4 class="text-dark font-weight-bolder">You are Offline</h4>
        <p class="text-secondary mb-0">Connectivity lost. Please do not close or refresh this page.</p>
    </div>
  </div>

  <!-- Blocker screen when app is updating to prevent broken state -->
  <transition name="fade">
    <div v-if="isAppUpdating" class="update-blocker-overlay">
      <div class="update-blocker-card text-center animate__animated animate__zoomIn">
        <div class="update-icon-container mb-3 animate__animated animate__pulse animate__infinite">
          <i class="fas fa-cloud-download-alt text-primary fa-3x"></i>
        </div>
        <h4 class="text-dark font-weight-bolder">
          {{ isAppBroken ? 'App Recovering' : 'App Update Available' }}
        </h4>
        <p class="text-secondary text-sm px-2 mb-0">
          {{ isAppBroken 
             ? 'We detected a loading issue and are restoring the app to a healthy state. This will only take a moment...' 
             : 'A new version of VECT suite is ready! Upgrade now to access the latest features, security patches, and performance enhancements.' 
          }}
        </p>
        
        <!-- Progress Bar (Shown when self-healing, or when actively applying upgrade) -->
        <div v-if="isAppBroken || isUpgrading" class="w-100 mt-4 px-2 animate__animated animate__fadeIn">
          <div class="progress" style="height: 8px;">
            <div class="progress-bar progress-bar-striped progress-bar-animated bg-primary" 
                 role="progressbar" 
                 :style="{ width: updateProgress + '%' }" 
                 :aria-valuenow="updateProgress" 
                 aria-valuemin="0" 
                 aria-valuemax="100">
            </div>
          </div>
          <div class="d-flex justify-content-between mt-2">
            <span class="text-xs text-muted font-weight-bold text-start" translate="no">
              {{ updateStatusText || 'Applying updates...' }}
            </span>
            <span class="text-xs text-primary font-weight-bolder text-end" translate="no">
              {{ Math.round(updateProgress) }}%
            </span>
          </div>
        </div>

        <!-- Optional Action Buttons (Shown when update is ready and NOT upgrading/broken) -->
        <div v-if="isUpdateReady && !isAppBroken && !isUpgrading" class="d-flex justify-content-center gap-2 mt-4 px-2">
          <button class="btn btn-sm btn-link text-secondary font-weight-bold px-4 mb-0" @click="postponeUpdate(true)">
            Later
          </button>
          <button class="btn btn-sm btn-primary rounded-pill px-4 mb-0" @click="startUpgrade(true)">
            <i class="fas fa-check-circle me-1"></i> Update Now ({{ countdownSeconds }}s)
          </button>
        </div>

        <!-- Network / Retry Fallback -->
        <div v-if="updateError" class="mt-4 border-top pt-3">
          <span class="text-danger text-xs d-block mb-3 font-weight-semibold">
            <i class="fas fa-exclamation-triangle me-1"></i> Update failed due to a network issue.
          </span>
          <button class="btn btn-sm btn-outline-danger rounded-pill px-4" @click="retryUpdate">
            <i class="fas fa-redo me-1"></i> Retry
          </button>
        </div>
      </div>
    </div>
  </transition>


  <!-- PWA Install Co-Promotion Modal -->
  <transition name="fade">
    <div v-if="showInstallPromotionModal" class="pwa-promo-overlay">
      <div class="pwa-promo-card animate__animated animate__zoomIn">
        <div class="pwa-promo-header text-center">
          <div class="pwa-icon-container mb-3">
            <i class="fas fa-cubes text-primary fa-3x"></i>
          </div>
          <h4 class="text-dark font-weight-bolder">Vardan Apps Suite</h4>
          <p class="text-secondary text-sm">Install the second PWA for the complete experience!</p>
        </div>
        <div class="pwa-promo-body py-2">
          <div v-if="installTargetApp === 'forms'" class="d-flex align-items-center mb-3 p-2 rounded bg-light border">
            <div class="avatar bg-success-soft text-success rounded-circle me-3 d-flex align-items-center justify-content-center" style="width: 48px; height: 48px; min-width: 48px;">
              <i class="fas fa-file-invoice fa-lg"></i>
            </div>
            <div>
              <h6 class="mb-0 text-sm font-weight-bold text-dark">Vardan Forms PWA</h6>
              <p class="text-xs text-muted mb-0">Fill, edit, and print service flow forms completely offline with absolute focus.</p>
            </div>
          </div>
          <div v-else class="d-flex align-items-center mb-3 p-2 rounded bg-light border">
            <div class="avatar bg-primary-soft text-primary rounded-circle me-3 d-flex align-items-center justify-content-center" style="width: 48px; height: 48px; min-width: 48px;">
              <i class="fas fa-tachometer-alt fa-lg"></i>
            </div>
            <div>
              <h6 class="mb-0 text-sm font-weight-bold text-dark">Vardan Dashboard PWA</h6>
              <p class="text-xs text-muted mb-0">Access complete admin tools, approvals, analytics, and settings from one screen.</p>
            </div>
          </div>
        </div>
        <div class="pwa-promo-footer d-flex gap-2 justify-content-end mt-3 border-top pt-3">
          <button class="btn btn-sm btn-link text-secondary mb-0" @click="showInstallPromotionModal = false">Later</button>
          <button class="btn btn-sm btn-primary mb-0 px-4 rounded-pill" @click="triggerInstallPromotion">
            <i class="fas fa-download me-1"></i> Install Now
          </button>
        </div>
      </div>
    </div>
  </transition>

  <!-- Dynamic Layout Switching -->

  <!-- This approach ensures that Dashboard dependencies are NOT part of the Form Mode dependency graph -->
  <component :is="activeLayout" />
</template>

<script setup>
import {computed, watch, defineAsyncComponent} from "vue";
import {useStore} from "vuex";
import {useRoute} from "vue-router";
const logo = "/img/logo.webp";
import ArgonAlert from "@/components/ArgonAlert.vue";
import ArgonButton from "@/components/ArgonButton.vue";

// Separate Layouts to stop loading unnecessary dependencies in Form Mode
const DashboardLayout = defineAsyncComponent(() => import("@/views/layouts/DashboardLayout.vue"));
const FormLayout = defineAsyncComponent(() => import("@/views/layouts/FormLayout.vue"));
const AuthAppRoot = defineAsyncComponent(() => import("./AuthApp.vue"));

const route = useRoute();
const store = useStore();

const showLoading = computed(() => store.state.showLoading);
const loadingStatus = computed(() => store.state.loadingStatus);
const isOnline = computed(() => store.state.isOnline);
const updateWaiting = computed(() => store.state.updateWaiting);

// App Switching Logic
const activeLayout = computed(() => {
  const path = route.path;
  
  // 1. Auth App URLs
  const authRoutes = ['/signin', '/logout', '/complete-profile', '/reset-password', '/access-denied'];
  if (authRoutes.some(r => path.startsWith(r))) {
    return AuthAppRoot;
  }

  // 2. Form App URLs
  const isFormView = (route.name === 'Service Flow Form' && route.params.id) || 
                     (route.name === 'Service Flow Template Form' && route.params.id) ||
                     (route.name === 'Form Service Flow Form' && route.params.id) || 
                     (route.name === 'Form Service Flow Templates' && route.params.id) ||
                     (route.name === 'Forms Hub') ||
                     path.startsWith('/form/');
  
  if (isFormView) {
    return FormLayout;
  }

  // 3. Guest App URLs
  if (route.meta.guest || window.location.pathname.startsWith('/guest/')) {
    return AuthAppRoot;
  }

  // 4. Default: Main App (Dashboard Layout)
  return DashboardLayout;
});

store.commit("setAppMode", process.env.VUE_APP_MODE);

const isFormMode = computed(() => {
  return (route.name === 'Service Flow Form' && route.params.id) || 
         (route.name === 'Service Flow Template Form' && route.params.id) ||
         (route.name === 'Form Service Flow Form' && route.params.id) || 
         (route.name === 'Form Service Flow Templates' && route.params.id) ||
         (route.name === 'Forms Hub') ||
         route.path.startsWith('/form/');
});

watch(isFormMode, (val) => {
  if (val) {
    document.body.classList.add('is-forced-fullscreen');
  } else {
    document.body.classList.remove('is-forced-fullscreen');
  }
}, { immediate: true });
</script>

<script>

export default {
  data() {
    return {
      isRefreshing: false,
      updateProgress: 0,
      progressInterval: null,
      // Draggable state
      isDragging: false,
      offlineCardPos: { x: 0, y: 0 },
      dragStart: { x: 0, y: 0 },
      updateCheckTimer: null,
      syncTimer: null,
      // PWA Co-Promotion State
      deferredPrompt: null,
      showInstallPromotionModal: false,
      installTargetApp: '',
      beforeInstallListener: null,
      appInstalledListener: null,
      // Self-healing App Update State
      isAppUpdating: false,
      isAppBroken: false,
      updateStatusText: '',
      updateError: null,
      showBlockerOnDownload: true,
      updateSearchTimeout: null,
      isUpdateReady: false,
      updatePostponed: false,
      isUpgrading: false,
      countdownSeconds: 60,
      countdownTimer: null,
      updateChannel: null,
    };
  },
  computed: {
    updateWaiting() {
        return this.$store.state.updateWaiting;
    }
  },
  created() {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (this.isRefreshing) return;
        this.isRefreshing = true;
        window.location.reload();
      });

      // Check if a Service Worker is already waiting (for refreshes/revisits/new tabs)
      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration && registration.waiting) {
          window.swRegistration = registration;
          this.handleUpdateWaiting(true);
        }
      });
    }

    // Initialize BroadcastChannel for cross-tab synchronization
    if (typeof window !== "undefined" && "BroadcastChannel" in window) {
      this.updateChannel = new BroadcastChannel('vect-pwa-channel');
      this.updateChannel.onmessage = (event) => {
        const { type } = event.data || {};
        if (type === 'downloading') {
          this.handleUpdateDownloading(false);
        } else if (type === 'waiting') {
          this.handleUpdateWaiting(false);
        } else if (type === 'postpone') {
          this.postponeUpdate(false);
        } else if (type === 'upgrade') {
          this.startUpgrade(false);
        }
      };
    }
    window.addEventListener("online", this.updateOnlineStatus);
    window.addEventListener("offline", this.updateOnlineStatus);
    window.addEventListener('sw-update-waiting', this.handleUpdateWaiting);
    window.addEventListener('sw-check-for-updates', this.checkForUpdates);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    window.addEventListener('sw-update-downloading', this.handleUpdateDownloading);
    window.addEventListener('sw-update-error', this.handleUpdateError);
    window.addEventListener('sw-force-update', this.handleForceUpdate);
    window.addEventListener('error', this.handleGlobalError, true);
    
    // Disable blocker showing for background check after 20 seconds
    setTimeout(() => {
      this.showBlockerOnDownload = false;
    }, 20000);
    
    // Proactively check for updates every 5 minutes
    this.updateCheckTimer = setInterval(this.checkForUpdates, 300000);
    // Initial check after a short delay
    setTimeout(this.checkForUpdates, 10000);

    // Recheck IndexedDB fingerprints and sidebar statistics every 15 seconds
    this.syncTimer = setInterval(this.syncFingerprintsAndStats, 15000);
    // Initial sync after a 15-second delay to not block initial load
    setTimeout(this.syncFingerprintsAndStats, 15000);

    // PWA Install Listeners
    this.beforeInstallListener = (e) => {
      e.preventDefault();
      this.deferredPrompt = e;
      window.deferredPrompt = e;
      
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('trigger_install') === 'true') {
        setTimeout(() => {
          if (this.deferredPrompt) {
            this.deferredPrompt.prompt();
            this.deferredPrompt = null;
          }
        }, 1200);
      }
    };
    
    this.appInstalledListener = () => {
      this.deferredPrompt = null;
      const isFormScope = window.location.pathname.startsWith('/form/');
      if (isFormScope) {
        this.installTargetApp = 'main';
        this.showInstallPromotionModal = true;
      } else {
        this.installTargetApp = 'forms';
        this.showInstallPromotionModal = true;
      }
    };

    window.addEventListener('beforeinstallprompt', this.beforeInstallListener);
    window.addEventListener('appinstalled', this.appInstalledListener);
  },
  beforeUnmount() {
    window.removeEventListener("online", this.updateOnlineStatus);
    window.removeEventListener("offline", this.updateOnlineStatus);
    window.removeEventListener('sw-update-waiting', this.handleUpdateWaiting);
    window.removeEventListener('sw-check-for-updates', this.checkForUpdates);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    window.removeEventListener('sw-update-downloading', this.handleUpdateDownloading);
    window.removeEventListener('sw-update-error', this.handleUpdateError);
    window.removeEventListener('sw-force-update', this.handleForceUpdate);
    window.removeEventListener('error', this.handleGlobalError, true);
    if (this.updateSearchTimeout) clearTimeout(this.updateSearchTimeout);
    if (this.progressInterval) clearInterval(this.progressInterval);
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    if (this.updateCheckTimer) clearInterval(this.updateCheckTimer);
    if (this.syncTimer) clearInterval(this.syncTimer);
    
    if (this.updateChannel) {
      this.updateChannel.close();
    }
    
    if (this.beforeInstallListener) {
      window.removeEventListener('beforeinstallprompt', this.beforeInstallListener);
    }
    if (this.appInstalledListener) {
      window.removeEventListener('appinstalled', this.appInstalledListener);
    }
  },
  methods: {
    triggerInstallPromotion() {
      this.showInstallPromotionModal = false;
      if (this.installTargetApp === 'forms') {
        window.open('/form/service-flows/1?trigger_install=true', '_blank');
      } else {
        window.open('/?trigger_install=true', '_blank');
      }
    },
    handleUpdateWaiting(broadcast = true) {
        if (this.isRefreshing || (navigator.serviceWorker && !navigator.serviceWorker.controller)) return;
        if (this.updatePostponed) return;
        
        this.isUpdateReady = true;
        this.isAppUpdating = true;

        if (broadcast && this.updateChannel) {
            this.updateChannel.postMessage({ type: 'waiting' });
        }

        this.startCountdown();
    },
    startCountdown() {
        if (this.countdownTimer) clearInterval(this.countdownTimer);
        this.countdownSeconds = 60;
        this.countdownTimer = setInterval(() => {
            if (this.countdownSeconds > 0) {
                this.countdownSeconds--;
            } else {
                clearInterval(this.countdownTimer);
                this.startUpgrade(true);
            }
        }, 1000);
    },
    postponeUpdate(broadcast = true) {
        this.isAppUpdating = false;
        this.updatePostponed = true;
        this.isUpdateReady = false;
        if (this.progressInterval) clearInterval(this.progressInterval);
        if (this.countdownTimer) clearInterval(this.countdownTimer);

        if (broadcast && this.updateChannel) {
            this.updateChannel.postMessage({ type: 'postpone' });
        }
    },
    startUpgrade(broadcast = true) {
        this.isUpgrading = true;
        this.updateStatusText = "Applying updates...";
        this.updateProgress = 0;
        if (this.countdownTimer) clearInterval(this.countdownTimer);

        if (broadcast && this.updateChannel) {
            this.updateChannel.postMessage({ type: 'upgrade' });
        }
        
        if (this.progressInterval) clearInterval(this.progressInterval);
        this.progressInterval = setInterval(() => {
            if (this.updateProgress < 100) {
                this.updateProgress += 10;
            } else {
                clearInterval(this.progressInterval);
                this.applyUpdate();
            }
        }, 80);
    },
    applyUpdate() {
        const registration = window.swRegistration;
        if (registration && registration.waiting) {
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        } else {
            // Fallback for unexpected states
            window.location.reload();
        }
    },
    checkForUpdates() {
        if ("serviceWorker" in navigator) {
            navigator.serviceWorker.ready.then((registration) => {
                // If we are forcing an update (i.e. isAppBroken is true), set a 4s fallback timer
                if (this.isAppBroken) {
                    if (this.updateSearchTimeout) clearTimeout(this.updateSearchTimeout);
                    this.updateSearchTimeout = setTimeout(() => {
                        if (this.isAppUpdating && !registration.installing && !registration.waiting) {
                            console.log("No new update installing/waiting but app is broken. Hard reloading as fallback...");
                            window.location.reload();
                        }
                    }, 4000);
                }
                registration.update().catch(err => {
                    console.warn("SW update check failed", err);
                    if (this.isAppBroken) {
                        window.location.reload();
                    }
                });
            });
        } else {
            if (this.isAppBroken) {
                window.location.reload();
            }
        }
    },
    handleUpdateDownloading(broadcast = true) {
        if (broadcast && this.updateChannel) {
            this.updateChannel.postMessage({ type: 'downloading' });
        }

        if (this.isAppBroken) {
            this.isAppUpdating = true;
            this.updateProgress = 0;
            this.updateError = null;
            this.updateStatusText = "Downloading latest updates...";
            
            if (this.progressInterval) clearInterval(this.progressInterval);
            this.progressInterval = setInterval(() => {
                if (this.updateProgress < 70) {
                    this.updateProgress += Math.random() * 8 + 5; // Fast initial download simulation
                } else if (this.updateProgress < 95) {
                    this.updateProgress += Math.random() * 2 + 1;  // Slower final steps
                } else {
                    this.updateProgress = 95;
                    clearInterval(this.progressInterval);
                }
            }, 500);
        }
    },
    handleUpdateError(e) {
        console.warn("Service Worker update error:", e.detail);
        if (this.isAppUpdating) {
            this.updateError = e.detail || "Network error occurred.";
            this.updateStatusText = "Failed to download update.";
        }
    },
    handleForceUpdate() {
        if (this.isAppUpdating && this.isAppBroken) return; // Already forcing
        this.isAppUpdating = true;
        this.isAppBroken = true;
        this.updateStatusText = "Chunk error detected. Updating app...";
        this.checkForUpdates();
    },
    handleGlobalError(event) {
        try {
            if (!event) return;
            
            var msg = event.message || '';
            var name = event.name || '';
            var isChunkError = false;
            
            if (typeof msg === 'string') {
                isChunkError = msg.indexOf('ChunkLoadError') > -1 || 
                               msg.indexOf('Loading chunk') > -1 || 
                               msg.indexOf('Failed to fetch') > -1;
            }
            if (typeof name === 'string' && name.indexOf('ChunkLoadError') > -1) {
                isChunkError = true;
            }
            
            var isLocalScriptError = false;
            if (event.target && typeof event.target.tagName === 'string') {
                var tagName = event.target.tagName.toUpperCase();
                if (tagName === 'SCRIPT' || tagName === 'LINK') {
                    var url = event.target.src || event.target.href || '';
                    if (url && (url.indexOf('.js') > -1 || url.indexOf('.css') > -1)) {
                        // Only trigger updates for local/same-origin assets, completely ignoring blocked third-party trackers or buttons
                        var isLocal = url.indexOf(window.location.origin) > -1 || url.indexOf('/') === 0 || !url.startsWith('http');
                        if (isLocal) {
                            isLocalScriptError = true;
                            console.warn('Fatal local asset load error caught:', url);
                        }
                    }
                }
            }
            
            if (isChunkError || isLocalScriptError) {
                console.warn('App dynamic chunk or local script load failure detected. Triggering self-healing...');
                this.handleForceUpdate();
            }
        } catch (e) {
            console.error('Error in global error handler:', e);
        }
    },
    retryUpdate() {
        this.isAppUpdating = true;
        this.updateProgress = 0;
        this.updateError = null;
        this.updateStatusText = "Checking and downloading updates...";
        
        if (this.progressInterval) clearInterval(this.progressInterval);
        this.progressInterval = setInterval(() => {
            if (this.updateProgress < 70) {
                this.updateProgress += Math.random() * 8 + 5;
            } else if (this.updateProgress < 95) {
                this.updateProgress += Math.random() * 2 + 1;
            } else {
                this.updateProgress = 95;
                clearInterval(this.progressInterval);
            }
        }, 500);
        
        this.checkForUpdates();
    },
    handleVisibilityChange() {
        if (document.visibilityState === "visible") {
            this.checkForUpdates();
        }
    },
    startDrag(e) {
        this.isDragging = true;
        const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
        const clientY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;
        
        this.dragStart = {
            x: clientX - this.offlineCardPos.x,
            y: clientY - this.offlineCardPos.y
        };
        
        const moveEvent = e.type.includes('touch') ? 'touchmove' : 'mousemove';
        const upEvent = e.type.includes('touch') ? 'touchend' : 'mouseup';
        
        window.addEventListener(moveEvent, this.doDrag, { passive: false });
        window.addEventListener(upEvent, this.stopDrag);
    },
    doDrag(e) {
        if (!this.isDragging) return;
        
        // Prevent scrolling while dragging on touch devices
        if (e.type === 'touchmove') e.preventDefault();
        
        const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
        const clientY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;
        
        this.offlineCardPos = {
            x: clientX - this.dragStart.x,
            y: clientY - this.dragStart.y
        };
    },
    stopDrag(e) {
        this.isDragging = false;
        const moveEvent = e.type.includes('touch') ? 'touchmove' : 'mousemove';
        const upEvent = e.type.includes('touch') ? 'touchend' : 'mouseup';
        
        window.removeEventListener(moveEvent, this.doDrag);
        window.removeEventListener(upEvent, this.stopDrag);
    },
    refreshApp() {
      window.location.reload();
    },
    updateOnlineStatus() {
      const status = navigator.onLine;
      this.$store.commit("updateOnlineStatus", status);
      if (status) {
          this.offlineCardPos = { x: 0, y: 0 };
          this.isDragging = false;
      }
    },
    async syncFingerprintsAndStats() {
      if (!this.$store.state.isAuthenticated) return;
      try {
        const { checkAndValidateFingerprints } = await import('@/utils/cacheService.js');
        await checkAndValidateFingerprints();
        
        await this.$store.dispatch('fetchCounts');
      } catch (e) {
        console.warn('[Sync] Periodic fingerprints & stats sync failed:', e);
      }
    }
  },
};
</script>

<style>
#app {
  overflow-x: hidden !important;
}

@media (max-width: 768px) {
  .table-responsive {
    display: block !important;
    width: 100% !important;
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch !important;
  }
  .table-responsive table th,
  .table-responsive table td {
    white-space: nowrap !important;
  }
}
p {
  white-space: normal !important;
}
.offline-banner {
  position: fixed;
  top: 0;
  width: 100%;
  background: red;
  color: white;
  text-align: center;
  padding: 10px;
}

@media (min-width: 1200px) {
  .ms-sidenav {
    margin-left: 14.25rem !important;
    transition: margin-left 0.2s ease-in-out;
  }

  #app.g-sidenav-hidden .ms-sidenav {
    margin-left: 5.5rem !important;
  }
}

@media (max-width: 1199.98px) {
  .ms-sidenav {
    margin-left: 0 !important;
  }
}

.content-wrapper .container-fluid {
  margin-left: 1rem !important;
  margin-right: 1rem !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
  width: auto !important;
}

@media (max-width: 575.98px) {
  .content-wrapper .container-fluid {
    margin-left: 0.5rem !important;
    margin-right: 0.5rem !important;
  }
}

body.is-forced-fullscreen .content-wrapper .container-fluid,
body.is-any-fullscreen .content-wrapper .container-fluid {
  margin-left: 0 !important;
  margin-right: 0 !important;
  width: 100% !important;
}

/* Fade effect */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}


/* Full-screen loading overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

/* Font Awesome spinner */
.loading-overlay i.fa-spinner {
  font-size: 3rem;
  color: white;
}

.loading-status-badge {
    font-size: 0.85rem;
    font-weight: 500;
    letter-spacing: 0.5px;
    padding: 0;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    color: rgba(255, 255, 255, 0.9) !important;
    text-shadow: 0 2px 4px rgba(0,0,0,0.5);
    text-transform: none !important; /* Remove uppercase for softer look */
}

.loading-status-badge i {
    font-size: 0.9rem;
    margin-right: 8px;
    color: #fff; /* White icon */
    opacity: 0.8;
}

/* Offline Blocker Overlay */
.offline-blocker {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.4); /* Very transparent white */
  backdrop-filter: blur(1px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20000; /* Above everything */
  cursor: not-allowed;
}

.offline-content {
  background: white;
  padding: 1.5rem 2.5rem 2.5rem 2.5rem;
  border-radius: 1.5rem;
  text-align: center;
  max-width: 400px;
  border-bottom: 5px solid #f5365c;
  pointer-events: auto;
  transition: transform 0.05s linear; /* Smooth minor jitters */
  box-shadow: 0 20px 50px rgba(0,0,0,0.15) !important;
}

.draggable-card {
  cursor: grab;
  user-select: none;
  touch-action: none;
}

.draggable-card:active {
  cursor: grabbing;
}

.drag-handle {
    cursor: grab;
    opacity: 0.3;
    font-size: 1.2rem;
    line-height: 1;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  z-index: 10001 !important;
  transition: opacity 0.3s ease-in-out;
}

.bg-fafafa {
  background-color: #fafafa !important;
}



.no-select {
    -webkit-touch-callout: none; /* iOS Safari */
    -webkit-user-select: none;   /* Safari */
    -khtml-user-select: none;    /* Konqueror HTML */
    -moz-user-select: none;      /* Old versions of Firefox */
    -ms-user-select: none;       /* Internet Explorer/Edge */
    user-select: none;           /* Non-prefixed version, currently supported by Chrome, Edge, Opera and Firefox */
    cursor: default;             /* Optional: Change cursor */
}

.v-toast {
    z-index: 10900 !important;
}

/* Global Avatar Fix: Ensure perfect circles and prevent squishing in flex containers */
.avatar {
  flex-shrink: 0 !important;
  aspect-ratio: 1 / 1 !important;
  object-fit: cover !important;
}

/* Global Card Styling */
.card {
  border-radius: 1rem !important;
}

/* Global Table Badge Standardization: Outlined Design */
.table .badge {
  background-image: none !important;
  background-color: transparent !important;
  border-width: 1px !important;
  border-style: solid !important;
  border-color: currentColor !important; /* Default to text color */
  box-shadow: none !important;
  font-weight: 600 !important;
  padding: 0.45em 0.75em !important;
  border-radius: 50rem !important; /* Capsule style */
}

/* Color Mappings for specific background classes to ensure text color is set */
.table .badge.bg-gradient-success,
.table .badge.badge-success,
.table .badge.bg-success {
  color: #2dce89 !important;
}

.table .badge.bg-gradient-info,
.table .badge.badge-info,
.table .badge.bg-info {
  color: #11cdef !important;
}

.table .badge.bg-gradient-warning,
.table .badge.badge-warning,
.table .badge.bg-warning {
  color: #fb6340 !important;
}

.table .badge.bg-gradient-danger,
.table .badge.badge-danger,
.table .badge.bg-danger {
  color: #f5365c !important;
}

.table .badge.bg-gradient-primary,
.table .badge.badge-primary,
.table .badge.bg-primary {
  color: #5e72e4 !important;
}

.table .badge.bg-gradient-secondary,
.table .badge.badge-secondary,
.table .badge.bg-secondary,
.table .badge.bg-gradient-light {
  color: #8392ab !important;
}

/* Dark Outlined */
.table .badge.bg-gradient-dark,
.table .badge.badge-dark,
.table .badge.bg-dark {
  color: #344767 !important;
}

/* Fix for badges that might have inherited text-dark but should have a specific color */
.table .badge.bg-gradient-light.text-dark {
  color: #8392ab !important;
}

/* PWA Promotion Modal Styles */
.pwa-promo-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.pwa-promo-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 28px;
  width: 90%;
  max-width: 480px;
  box-shadow: 0 15px 35px rgba(50, 50, 93, 0.1), 0 5px 15px rgba(0, 0, 0, 0.07);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.pwa-icon-container {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 76px;
  height: 76px;
  background: rgba(94, 114, 228, 0.1);
  border-radius: 50%;
  color: #5e72e4;
}

.bg-success-soft {
  background-color: rgba(45, 206, 137, 0.12) !important;
}

.bg-primary-soft {
  background-color: rgba(94, 114, 228, 0.12) !important;
}

.gap-2 {
  gap: 8px;
}

/* Global Disabled Action Buttons in Tables: Grayed out */
.table td button:disabled, 
.table td .btn:disabled,
.table td button[disabled], 
.table td .btn[disabled] {
  background-color: transparent !important;
  border-color: #adb5bd !important;
  color: #adb5bd !important;
  opacity: 0.45 !important;
  cursor: not-allowed !important;
}

/* Blocker Overlay for PWA Updates */
.update-blocker-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 30000 !important;
}

.update-blocker-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 35px 25px;
  width: 90%;
  max-width: 440px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08), 0 5px 15px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.update-icon-container {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  background: rgba(94, 114, 228, 0.1);
  border-radius: 50%;
  color: #5e72e4;
}
</style>